import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "../../auth/AuthProvider";
import Sidebar from "../../../core/common/sidebar/sidebar";
import Breadcrumb from "../../../core/common/Breadcrumb/breadcrumb";
import { all_routes as routes } from "../../router/all_routes";
import styles from "./styles/UserPreferences.module.css";
import { cityOptions } from "../../flight/cityOptions";

const idiomaOptions = [
    { value: "english", label: "English" },
    { value: "italian", label: "Italian" },
    { value: "french", label: "French" },
    { value: "german", label: "German" },
    { value: "portuguese", label: "Portuguese" },
    { value: "russian", label: "Russian" },
    { value: "hebrew", label: "Hebrew" },
    { value: "mandarin", label: "Mandarin" },
    { value: "japanese", label: "Japanese" },
    { value: "ukrainian", label: "Ukrainian" },
    { value: "spanish", label: "Spanish" },
    { value: "dutch", label: "Dutch" },
    { value: "swedish", label: "Swedish" },
    { value: "norwegian", label: "Norwegian" },
    { value: "danish", label: "Danish" },
    { value: "polish", label: "Polish" },
    { value: "greek", label: "Greek" },
    { value: "turkish", label: "Turkish" },
    { value: "czech", label: "Czech" },
    { value: "hungarian", label: "Hungarian" },
    { value: "romanian", label: "Romanian" },
    { value: "finnish", label: "Finnish" },
    { value: "slovak", label: "Slovak" },
    { value: "bulgarian", label: "Bulgarian" },
    { value: "croatian", label: "Croatian" },
    { value: "irish", label: "Irish" },
    { value: "welsh", label: "Welsh" },
    { value: "scottish_gaelic", label: "Scottish Gaelic" },
    { value: "basque", label: "Basque" },
    { value: "catalan", label: "Catalan" },
    { value: "galician", label: "Galician" },
];

const UserPreferences: React.FC = () => {
    const BASE_URL = process.env.REACT_APP_API_URL;
    const { user } = useAuth();

    const [language, setLanguage] = useState("");
    const [city, setCity] = useState("");
    const [people, setPeople] = useState<number | undefined>();
    const [serviceId, setServiceId] = useState<number | undefined>();
    const [services, setServices] = useState<{ id: number; nome: string }[]>([]);

    useEffect(() => {
        axios.get(`${BASE_URL}/servicos`)
            .then((res) => {
                setServices(res.data); // res.data é array de {id, nome, ...}
            })
            .catch((err) => console.error("Failed to load services:", err));
    }, [BASE_URL]);

    useEffect(() => {
        const token = localStorage.getItem("token");
        if (!token) return;

        axios
            .get(`${BASE_URL}/preferencias`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => {
                const prefs = res.data;
                setLanguage(prefs.idioma || "");
                setCity(prefs.cidade || "");
                setPeople(prefs.pessoas || undefined);
                setServiceId(prefs.servicoId || undefined);
            })
            .catch(() => { });
    }, [BASE_URL]);

    const handleSave = () => {
        const token = localStorage.getItem("token");
        if (!token) return;

        axios
            .post(
                `${BASE_URL}/preferencias`,
                { idioma: language, cidade: city, pessoas: people, servicoId: serviceId },
                { headers: { Authorization: `Bearer ${token}` } }
            )
            .then(() => alert("Preferences saved successfully!"))
            .catch(() => alert("Error while saving preferences."));
    };

    const breadcrumbs = [
        { label: "Dashboard", link: routes.userDashboard },
        { label: "Preferences", link: routes.userPreferences },
    ];

    return (
        <div>
            <Breadcrumb
                title="Preferences"
                breadcrumbs={breadcrumbs}
                backgroundClass="breadcrumb-bg-04"
            />

            {/* Page Wrapper */}
            <div className="content">
                <div className="container">
                    <div className="row">
                        {/* Sidebar */}
                        <div className="col-xl-3 col-lg-4">
                            <Sidebar />
                        </div>
                        {/* /Sidebar */}

                        {/* Preferences Settings */}
                        <div className="col-xl-9 col-lg-8">
                            <div className="card settings mb-0">
                                <div className="card-header">
                                    <h6>Preferences Settings</h6>
                                </div>
                                <div className="card-body pb-1">
                                    <div className="settings-link d-flex align-items-center flex-wrap">
                                        <Link to={routes.profileSettings}>
                                            <i className="isax isax-user-octagon me-2" />
                                            Profile Settings
                                        </Link>
                                        <Link to={routes.securitySettings}>
                                            <i className="isax isax-shield-tick me-2" />
                                            Security
                                        </Link>
                                        <Link to={routes.notificationSettings}>
                                            <i className="isax isax-notification me-2" />
                                            Notifications
                                        </Link>
                                        <Link
                                            to={routes.userPreferences}
                                            className="active ps-3 pe-3"
                                        >
                                            <i className="isax isax-setting-2 me-2" />
                                            Preferences
                                        </Link>
                                        <Link to={routes.integrationSettings}>
                                            <i className="isax isax-hierarchy me-2" />
                                            Integrations
                                        </Link>
                                    </div>

                                    {/* Intro Text */}
                                    <div className={styles.formIntro}>
                                        This page allows you to set your <strong>search preferences</strong>. These preferences help us
                                        personalize your search results, services, and recommendations.
                                    </div>

                                    <div className="row mt-4">
                                        <div className="col-lg-12">
                                            {/* Language */}
                                            {/* Language */}
                                            <div className={styles.fieldBlock}>
                                                <p className={styles.fieldDescription}>
                                                    Select your preferred <strong>language</strong>. This will be used to personalize your searches and recommendations.
                                                </p>
                                                <label className={styles.formLabel}>Language</label>
                                                <select
                                                    className="form-control"
                                                    value={language}
                                                    onChange={(e) => setLanguage(e.target.value)}
                                                >
                                                    <option value="">Select a language</option>
                                                    {idiomaOptions.map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            {/* City */}
                                            <div className={styles.fieldBlock}>
                                                <p className={styles.fieldDescription}>
                                                    Define your <strong>city</strong> so we can provide more accurate search results and suggestions.
                                                </p>
                                                <label className={styles.formLabel}>City</label>
                                                <select
                                                    className="form-control"
                                                    value={city}
                                                    onChange={(e) => setCity(e.target.value)}
                                                >
                                                    <option value="">Select a city</option>
                                                    {cityOptions.map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            {/* People & Service Row */}
                                            <div className={styles.flexRow}>
                                                <div className={styles.halfField}>
                                                    <p className={styles.fieldDescription}>
                                                        How many <strong>people</strong> usually join you? This helps us filter services and accommodations.
                                                    </p>
                                                    <label className={styles.formLabel}>Number of People</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={people || ""}
                                                        onChange={(e) =>
                                                            setPeople(e.target.value ? Number(e.target.value) : undefined)
                                                        }
                                                    />
                                                </div>

                                                <div className={styles.halfField}>
                                                    <p className={styles.fieldDescription}>
                                                        Choose the <strong>service</strong> you use most often. It will be pre-filled when searching for services.
                                                    </p>
                                                    <label className={styles.formLabel}>Service</label>
                                                    <select
                                                        className="form-control"
                                                        value={serviceId || ""}
                                                        onChange={(e) => setServiceId(e.target.value ? Number(e.target.value) : undefined)}
                                                    >
                                                        <option value="">Select a service</option>
                                                        {services.map((service) => (
                                                            <option key={service.id} value={service.id}>
                                                                {service.nome}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>

                                            <button className={styles.saveButton} onClick={handleSave}>
                                                Save Preferences
                                            </button>
                                        </div>
                                    </div>
                                    {/* /Preferences Form */}
                                </div>
                            </div>
                        </div>
                        {/* /Preferences Settings */}
                    </div>
                </div>
            </div>
            {/* /Page Wrapper */}
        </div>
    );
};

export default UserPreferences;